import * as React from "react";
import { Save } from "lucide-react";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { TextInput } from "../../../ui/components/Form";
import { Modal } from "../../../ui/components/Modal";
import * as adminUsersApi from "../../lib/api/admin-users";
import { ApiClientError } from "../../lib/api/client";
import { ProApiClientError } from "../../lib/api/pro-api.client";
import { useI18n } from "../../lib/i18n";
import * as confirmStyles from "../organizations/OrganizationDetailPage.css";
import * as formStyles from "../organizations/OrganizationEditPage.css";

function getErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiClientError || e instanceof ProApiClientError) {
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return fallback;
}

export function EditUserIdentityModal({
  open,
  setOpen,
  userId,
  firstName: initialFirstName,
  lastName: initialLastName,
  userLabel,
  token,
  onSaved,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  userId: string;
  firstName: string;
  lastName: string;
  userLabel: string;
  token: string | null;
  onSaved?: (next: { firstName: string; lastName: string }) => void | Promise<void>;
}) {
  const { t } = useI18n();
  const [firstName, setFirstName] = React.useState(initialFirstName);
  const [lastName, setLastName] = React.useState(initialLastName);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setFirstName(initialFirstName);
    setLastName(initialLastName);
    setSaving(false);
    setError(null);
  }, [open, initialFirstName, initialLastName]);

  const dirty =
    firstName.trim() !== initialFirstName.trim() ||
    lastName.trim() !== initialLastName.trim();

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
  };

  const handleSave = async () => {
    if (!token || !dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      const next = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      };
      await adminUsersApi.patchAdminUser(token, userId, next);
      await onSaved?.(next);
      setOpen(false);
    } catch (e) {
      setError(getErrorMessage(e, "Failed to save."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(next) => {
        if (!next) handleClose();
        else setOpen(true);
      }}
      minWidth="420px"
      minHeight="auto"
      maxWidth="520px"
    >
      <div className={confirmStyles.confirmModalBody}>
        <h3 className={confirmStyles.confirmModalTitle}>{t("pro.users.editUser")}</h3>
        <p className={confirmStyles.confirmModalText}>{userLabel}</p>

        {error ? <Callout variant="error">{error}</Callout> : null}

        <div className={formStyles.twoColRow}>
          <TextInput
            label={t("pro.users.firstName")}
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setError(null);
            }}
            disabled={saving}
            changed={firstName.trim() !== initialFirstName.trim()}
            autoComplete="given-name"
          />
          <TextInput
            label={t("pro.users.lastName")}
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setError(null);
            }}
            disabled={saving}
            changed={lastName.trim() !== initialLastName.trim()}
            autoComplete="family-name"
          />
        </div>

        <div className={confirmStyles.confirmModalActions}>
          <Button
            color="neutral"
            variant="outline"
            size="md"
            disabled={saving}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="solid"
            size="md"
            iconLeft={<Save size={16} />}
            loading={saving}
            disabled={!dirty || !token}
            onClick={() => void handleSave()}
          >
            Save changes
          </Button>
        </div>
      </div>
    </Modal>
  );
}
